import type { ContentPublicState, ContentType } from "./types";

/** Etiquetas y color de cada tipo: los mismos que la app (culture.js). */
export const TYPE_META: Record<ContentType, { label: string; color: string; gradient: [string, string] }> = {
  NEWS: { label: "Noticia", color: "#2563EB", gradient: ["#2563EB", "#06B6D4"] },
  RELEASE: { label: "Lanzamiento", color: "#FF2D8E", gradient: ["#FF2D8E", "#C026D3"] },
  MIX: { label: "DJ set", color: "#7C3AED", gradient: ["#7C3AED", "#4338CA"] },
  INTERVIEW: { label: "Entrevista", color: "#F59E0B", gradient: ["#F59E0B", "#EA580C"] },
  SCENE_REPORT: { label: "Reportaje", color: "#10B981", gradient: ["#10B981", "#059669"] },
  VIDEO: { label: "Video", color: "#FB6514", gradient: ["#FB6514", "#F43F5E"] },
  AGENDA: { label: "Agenda", color: "#06B6D4", gradient: ["#06B6D4", "#0EA5E9"] },
};

export const TYPE_LABELS: Record<ContentType, string> = Object.fromEntries(
  Object.entries(TYPE_META).map(([k, v]) => [k, v.label]),
) as Record<ContentType, string>;

export const STATE_LABELS: Record<ContentPublicState, string> = {
  draft: "Borrador",
  scheduled: "Programado",
  published: "Publicado",
  archived: "Archivado",
};

/** Qué enlace pide cada tipo (la app lo reproduce o lo muestra según el sitio). */
export function sourceFieldFor(type: ContentType): { label: string; placeholder: string } {
  if (type === "MIX" || type === "RELEASE") {
    return { label: "Tema o set (SoundCloud)", placeholder: "https://soundcloud.com/artista/tema" };
  }
  if (type === "VIDEO") {
    return { label: "Video (YouTube o Vimeo)", placeholder: "https://www.youtube.com/watch?v=…" };
  }
  return {
    label: "Enlace (opcional: SoundCloud, YouTube, Vimeo, Mixcloud o Spotify)",
    placeholder: "https://…",
  };
}

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

/** «8 de junio de 2026» en hora de Chile, como la app. */
export function dateLabel(iso: string | null | undefined): string {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Santiago",
    day: "numeric",
    month: "numeric",
    year: "numeric",
  }).formatToParts(d);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  return `${get("day")} de ${MESES[get("month") - 1]} de ${get("year")}`;
}

/** «jue 9 oct, 10:00» en hora de Chile (para «Programado»). */
export function dateTimeLabel(iso: string | null | undefined): string {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("es-CL", {
    timeZone: "America/Santiago",
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

/** Estado público; con un backend anterior a backend#115 se deduce de la fecha. */
export function stateOf(c: { status: string; publishedAt: string | null; publicState?: ContentPublicState }): ContentPublicState {
  if (c.publicState) return c.publicState;
  if (c.status === "archived") return "archived";
  if (c.status !== "published") return "draft";
  return c.publishedAt && new Date(c.publishedAt).getTime() > Date.now() ? "scheduled" : "published";
}
